import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Chip, Stack, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, OutlinedInput,
  ToggleButtonGroup, ToggleButton, Tooltip, Alert, SelectChangeEvent,
  Button, Popover, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TuneIcon from '@mui/icons-material/Tune';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SortIcon from '@mui/icons-material/Sort';
import { useLocation } from 'react-router-dom';
import { useCoordinateContext } from '~/hooks/coordinate/useCoordinateContext';
import {
  DispositionType, CoordinationStatus, UrgencyLevel,
  DISPOSITION_LABELS,
} from '~/types/coordinate';
import CoordinateSplitView from '~/components/coordinate/patient-list/CoordinateSplitView';
import CoordinatePatientTable from '~/components/coordinate/patient-list/CoordinatePatientTable';
import CoordinateBoardView from '~/components/coordinate/patient-list/CoordinateBoardView';

type ViewMode = 'split' | 'table' | 'board';
type SortBy = 'default' | 'urgency' | 'los' | 'status' | 'name';
type AiFilter = null | 'pending_drafts' | 'active_outreach' | 'insurance_issues';

const URGENCY_ORDER: Record<string, number> = { emergent: 0, urgent: 1, routine: 2 };
const STATUS_ORDER: Record<CoordinationStatus, number> = {
  confirm_failed: 0, declined: 1, negotiating: 2, confirmed: 3,
  awaiting_response: 4, matched: 5, confirming: 6, matching: 7, pending: 8, accepted: 9,
};

const DEMO_TODAY_MS = new Date('2026-05-01').getTime();

function losRemainingDays(admitDate: string, losTargetDays: number): number {
  const [m, d, y] = admitDate.split('/').map(Number);
  const target = new Date(y, m - 1, d).getTime() + losTargetDays * 86_400_000;
  return Math.round((target - DEMO_TODAY_MS) / 86_400_000);
}

const STATUS_OPTIONS: { value: CoordinationStatus | 'attention'; label: string }[] = [
  { value: 'pending',           label: 'Pending' },
  { value: 'matched',           label: 'Destination Identified' },
  { value: 'confirmed',         label: 'Ready' },
  { value: 'awaiting_response', label: 'Awaiting Response' },
  { value: 'accepted',          label: 'Accepted' },
  { value: 'attention',         label: '⚠ Needs Attention' },
];

const URGENCY_OPTIONS: { value: UrgencyLevel; label: string }[] = [
  { value: 'routine',  label: 'Routine' },
  { value: 'urgent',   label: 'Urgent' },
  { value: 'emergent', label: 'Emergent' },
];

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'urgency', label: 'Urgency' },
  { value: 'los',     label: 'LOS Remaining' },
  { value: 'status',  label: 'Status' },
  { value: 'name',    label: 'Name' },
];

const renderMultiValue = (selected: string[], allLabel: string) => {
  if (selected.length === 0) return allLabel;
  if (selected.length === 1) return selected[0];
  return `${selected.length} selected`;
};

// Demo: hardcoded "current user" for the Coordinate prototype. The Predict side
// derives this from auth; Coordinate doesn't yet, so we pin to the assignee that
// owns most of the demo cases.
const DEMO_CURRENT_USER = 'Sarah Johnson';

interface CoordinatePatientListProps {
  ownershipFilter?: 'ALL' | 'MY';
}

const CoordinatePatientList: React.FC<CoordinatePatientListProps> = ({ ownershipFilter = 'ALL' }) => {
  const { patients: allPatients } = useCoordinateContext();
  const location = useLocation();

  const patients = useMemo(
    () => ownershipFilter === 'MY'
      ? allPatients.filter(p => p.assignedTo === DEMO_CURRENT_USER)
      : allPatients,
    [allPatients, ownershipFilter],
  );

  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dispositionFilter, setDispositionFilter] = useState<DispositionType[]>([]);
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyLevel[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('default');
  const [aiFilter, setAiFilter] = useState<AiFilter>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // Pre-apply status filter from router state (e.g., from KPIs page)
  useEffect(() => {
    const state = location.state as { statusFilter?: string[] } | null;
    if (state?.statusFilter?.length) {
      setStatusFilter(state.statusFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allAssignees = useMemo(
    () => [...new Set(patients.map(p => p.assignedTo))].sort(),
    [patients],
  );

  const needsAttentionCount = patients.filter(
    p => p.coordinationStatus === 'confirm_failed' || p.coordinationStatus === 'declined'
  ).length;

  const aiStats = useMemo(() => {
    let pendingDecisions = 0;
    let activeOutreach = 0;
    let insuranceIssues = 0;
    patients.forEach(p => {
      if ((p.agentDrafts ?? []).some(d => d.status === 'pending')) pendingDecisions++;
      if (p.providerOutreach?.attempts.some(a => a.status === 'calling')) activeOutreach++;
      if (p.insuranceTrack?.status === 'issue' || p.insuranceTrack?.status === 'pending') insuranceIssues++;
    });
    return { pendingDecisions, activeOutreach, insuranceIssues };
  }, [patients]);

  const handleStatusChange = (e: SelectChangeEvent<string[]>) => {
    setStatusFilter(e.target.value as string[]);
  };
  const handleDispositionChange = (e: SelectChangeEvent<DispositionType[]>) => {
    setDispositionFilter(e.target.value as DispositionType[]);
  };
  const handleUrgencyChange = (e: SelectChangeEvent<UrgencyLevel[]>) => {
    setUrgencyFilter(e.target.value as UrgencyLevel[]);
  };
  const handleAssigneeChange = (e: SelectChangeEvent<string[]>) => {
    setAssigneeFilter(e.target.value as string[]);
  };

  const activeFilterCount = statusFilter.length + dispositionFilter.length + urgencyFilter.length + assigneeFilter.length + (aiFilter ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter([]);
    setDispositionFilter([]);
    setUrgencyFilter([]);
    setAssigneeFilter([]);
    setAiFilter(null);
  };

  const toggleAiFilter = (f: AiFilter) => {
    setAiFilter(prev => prev === f ? null : f);
  };

  const filtered = useMemo(() => {
    let result = patients.filter(p => {
      if (statusFilter.length > 0) {
        const isAttention = p.coordinationStatus === 'confirm_failed' || p.coordinationStatus === 'declined';
        const wantsAttention = statusFilter.includes('attention');
        const wantsStatus = statusFilter.filter(s => s !== 'attention').includes(p.coordinationStatus);
        if (!(wantsStatus || (wantsAttention && isAttention))) return false;
      }
      if (dispositionFilter.length > 0 && !dispositionFilter.includes(p.disposition)) return false;
      if (urgencyFilter.length > 0 && !urgencyFilter.includes(p.urgency)) return false;
      if (assigneeFilter.length > 0 && !assigneeFilter.includes(p.assignedTo)) return false;
      if (aiFilter === 'pending_drafts' && !(p.agentDrafts ?? []).some(d => d.status === 'pending')) return false;
      if (aiFilter === 'active_outreach' && !p.providerOutreach?.attempts.some(a => a.status === 'calling')) return false;
      if (aiFilter === 'insurance_issues' && p.insuranceTrack?.status !== 'issue' && p.insuranceTrack?.status !== 'pending') return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (
          !p.name.toLowerCase().includes(q) &&
          !p.facilityName.toLowerCase().includes(q) &&
          !p.insurance.toLowerCase().includes(q) &&
          !p.assignedTo.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });

    if (sortBy === 'urgency') {
      result = [...result].sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 9) - (URGENCY_ORDER[b.urgency] ?? 9));
    } else if (sortBy === 'los') {
      result = [...result].sort((a, b) =>
        losRemainingDays(a.admitDate, a.losTargetDays) - losRemainingDays(b.admitDate, b.losTargetDays)
      );
    } else if (sortBy === 'status') {
      result = [...result].sort((a, b) => (STATUS_ORDER[a.coordinationStatus] ?? 9) - (STATUS_ORDER[b.coordinationStatus] ?? 9));
    } else if (sortBy === 'name') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    }

    return result;
  }, [patients, statusFilter, dispositionFilter, urgencyFilter, assigneeFilter, aiFilter, searchQuery, sortBy]);

  const hasFilters = activeFilterCount > 0 || searchQuery;
  const countLabel = hasFilters
    ? `${filtered.length} of ${patients.length} patients`
    : `${patients.length} patients`;

  const attentionSelected = statusFilter.includes('attention');
  const filterOpen = Boolean(filterAnchorEl);

  return (
    <Box sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 }, display: 'flex', flexDirection: 'column' }}>
      {/* Single header row: title | search + filters button + view toggle */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography variant="h4" fontWeight={600}>
            {ownershipFilter === 'MY' ? 'My Patients' : 'All Patients'}
          </Typography>
          <Typography variant="body2" color="text.secondary">{countLabel}</Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {/* Search — always visible */}
          <TextField
            size="small"
            placeholder="Search patients…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ width: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Filters button */}
          <Button
            size="small"
            variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
            startIcon={<TuneIcon sx={{ fontSize: '1rem' }} />}
            onClick={e => setFilterAnchorEl(e.currentTarget)}
            sx={{
              fontWeight: 600, fontSize: '0.78rem', px: 1.5, height: 40,
              ...(activeFilterCount > 0
                ? {}
                : { borderColor: 'divider', color: 'text.secondary' }),
            }}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>

          {/* Filters popover */}
          <Popover
            open={filterOpen}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 0.5, width: 300, p: 2 } }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>Filters</Typography>
              {activeFilterCount > 0 && (
                <Button size="small" onClick={clearFilters} sx={{ fontSize: '0.72rem', py: 0, minWidth: 0 }}>
                  Clear all
                </Button>
              )}
            </Stack>

            <Stack spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink={statusFilter.length > 0} sx={{ bgcolor: 'background.paper', px: 0.5 }}>
                  Status{statusFilter.length > 0 ? ` (${statusFilter.length})` : ''}
                </InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  onChange={handleStatusChange}
                  input={<OutlinedInput label={`Status${statusFilter.length > 0 ? ` (${statusFilter.length})` : ''}`} />}
                  renderValue={selected => renderMultiValue(selected, 'All statuses')}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel shrink={dispositionFilter.length > 0} sx={{ bgcolor: 'background.paper', px: 0.5 }}>
                  Disposition{dispositionFilter.length > 0 ? ` (${dispositionFilter.length})` : ''}
                </InputLabel>
                <Select
                  multiple
                  value={dispositionFilter}
                  onChange={handleDispositionChange}
                  input={<OutlinedInput label={`Disposition${dispositionFilter.length > 0 ? ` (${dispositionFilter.length})` : ''}`} />}
                  renderValue={selected => renderMultiValue(selected.map(d => DISPOSITION_LABELS[d]), 'All dispositions')}
                >
                  {(Object.keys(DISPOSITION_LABELS) as DispositionType[]).map(d => (
                    <MenuItem key={d} value={d}>{DISPOSITION_LABELS[d]}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel shrink={urgencyFilter.length > 0} sx={{ bgcolor: 'background.paper', px: 0.5 }}>
                  Urgency{urgencyFilter.length > 0 ? ` (${urgencyFilter.length})` : ''}
                </InputLabel>
                <Select
                  multiple
                  value={urgencyFilter}
                  onChange={handleUrgencyChange}
                  input={<OutlinedInput label={`Urgency${urgencyFilter.length > 0 ? ` (${urgencyFilter.length})` : ''}`} />}
                  renderValue={selected => renderMultiValue(
                    selected.map(u => u.charAt(0).toUpperCase() + u.slice(1)),
                    'All urgencies',
                  )}
                >
                  {URGENCY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel shrink={assigneeFilter.length > 0} sx={{ bgcolor: 'background.paper', px: 0.5 }}>
                  Assigned to{assigneeFilter.length > 0 ? ` (${assigneeFilter.length})` : ''}
                </InputLabel>
                <Select
                  multiple
                  value={assigneeFilter}
                  onChange={handleAssigneeChange}
                  input={<OutlinedInput label={`Assigned to${assigneeFilter.length > 0 ? ` (${assigneeFilter.length})` : ''}`} />}
                  renderValue={selected => renderMultiValue(selected, 'All assignees')}
                >
                  {allAssignees.map(a => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider />

              <FormControl size="small" fullWidth>
                <InputLabel sx={{ bgcolor: 'background.paper', px: 0.5 }}>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortBy)}
                  input={<OutlinedInput label="Sort by" />}
                  startAdornment={<SortIcon sx={{ ml: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                >
                  {SORT_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {activeFilterCount > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Showing {filtered.length} of {patients.length} patients
                </Typography>
              </>
            )}
          </Popover>

          {/* View mode toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <Tooltip title="Split view">
              <ToggleButton value="split" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <VerticalSplitIcon sx={{ fontSize: '1rem' }} /> Split
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Table view">
              <ToggleButton value="table" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <TableRowsIcon sx={{ fontSize: '1rem' }} /> Table
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Board view">
              <ToggleButton value="board" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <ViewColumnIcon sx={{ fontSize: '1rem' }} /> Board
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* AI Activity Banner — chips are clickable quick-filters */}
      {(aiStats.pendingDecisions > 0 || aiStats.activeOutreach > 0 || aiStats.insuranceIssues > 0) && (
        <Box
          sx={{
            mb: 2, px: 2, py: 1.25,
            bgcolor: '#f0eef8',
            border: '1px solid',
            borderColor: 'rgba(123,31,162,0.25)',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: '#635f99' }} />
            <Typography variant="caption" fontWeight={700} color="#635f99">
              Coordinate AI
            </Typography>
          </Stack>
          {aiStats.pendingDecisions > 0 && (
            <Tooltip title="Click to filter patients with pending AI decisions" placement="top" arrow>
              <Chip
                label={`${aiStats.pendingDecisions} pending decision${aiStats.pendingDecisions !== 1 ? 's' : ''}`}
                size="small"
                onClick={() => toggleAiFilter('pending_drafts')}
                sx={{
                  height: 20, fontSize: '0.68rem', fontWeight: 600,
                  bgcolor: aiFilter === 'pending_drafts' ? '#635f99' : '#f0eef8',
                  color: aiFilter === 'pending_drafts' ? '#fff' : '#635f99',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          {aiStats.activeOutreach > 0 && (
            <Tooltip title="Click to filter patients with active AI outreach calls" placement="top" arrow>
              <Chip
                label={`${aiStats.activeOutreach} outreach call${aiStats.activeOutreach !== 1 ? 's' : ''} in progress`}
                size="small"
                onClick={() => toggleAiFilter('active_outreach')}
                sx={{
                  height: 20, fontSize: '0.68rem', fontWeight: 600,
                  bgcolor: aiFilter === 'active_outreach' ? '#1565c0' : '#e3f2fd',
                  color: aiFilter === 'active_outreach' ? '#fff' : '#1565c0',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          {aiStats.insuranceIssues > 0 && (
            <Tooltip title="Click to filter patients with insurance issues" placement="top" arrow>
              <Chip
                label={`${aiStats.insuranceIssues} insurance issue${aiStats.insuranceIssues !== 1 ? 's' : ''}`}
                size="small"
                onClick={() => toggleAiFilter('insurance_issues')}
                sx={{
                  height: 20, fontSize: '0.68rem', fontWeight: 600,
                  bgcolor: aiFilter === 'insurance_issues' ? '#e65100' : '#fff3e0',
                  color: aiFilter === 'insurance_issues' ? '#fff' : '#e65100',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          {aiFilter ? (
            <Button size="small" onClick={() => setAiFilter(null)} sx={{ ml: 'auto', fontSize: '0.68rem', py: 0, minWidth: 0, color: '#635f99' }}>
              Clear filter
            </Button>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
              Click a chip to filter
            </Typography>
          )}
        </Box>
      )}

      {/* Needs Attention explanation — shown when that filter is active */}
      {attentionSelected && needsAttentionCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong>Needs Attention</strong> includes patients where the case manager must act before the referral can progress:
            a provider <strong>declined the referral</strong>, or a provider <strong>failed the availability check</strong> during confirmation.
            In both cases, return to the patient and select a new provider.
          </Typography>
        </Alert>
      )}

      {/* View */}
      {viewMode === 'split'   && <CoordinateSplitView patients={filtered} />}
      {viewMode === 'table'   && <CoordinatePatientTable patients={filtered} />}
      {viewMode === 'board'   && <CoordinateBoardView patients={filtered} />}
    </Box>
  );
};

export default CoordinatePatientList;
